import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'

dayjs.extend(utc)
import {
  EVENT_TYPE_ASSESSMENT,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  GENDER_LIST_ONBOARDING,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import { LSVideoPartHeaderHeading } from '../LiveSessionPageStyles'

export const useDebugMode = () => {
  const [debugModeClick, setDebugModeClick] = useState(0)

  const handleDebugModeClick = () => {
    setDebugModeClick((prev) => prev + 1)
  }

  useEffect(() => {
    if (debugModeClick > 10) {
      localStorage.setItem('debugMode', 'true')
      window.location.reload()
    }
  }, [debugModeClick])

  return handleDebugModeClick
}

export function useTopHeadingFirstText({
  eventType,
  prescriptionData,
  therapyReport,
  screeningReport,
  assessmentReport,
  bookedEventFinalData,
}) {
  const finalObject = {
    left: '',
    right: bookedEventFinalData?.feePaid
      ? `Fee Paid: ${getPriceStringWithCurrency(
          bookedEventFinalData?.feePaid,
          bookedEventFinalData?.currency,
          true,
        )}`
      : '',
  }

  let patientName
  let patientAge
  let patientGender
  let assessmentTitleForTop

  switch (eventType) {
    case EVENT_TYPE_CONSULTATION:
      patientName = prescriptionData?.patientPrefill?.name
      patientAge = getAgeFromDobWithTime(prescriptionData?.patientPrefill?.vital?.dateOfBirth)
      patientGender = GENDER_LIST_ONBOARDING?.find(
        (item) => item.value === prescriptionData?.patientPrefill?.vital?.gender,
      )?.label
      break

    case EVENT_TYPE_THERAPY_SESSION:
      patientName = therapyReport?.patientName
      patientAge = getAgeFromDobWithTime(therapyReport?.patientDateOfBirth)
      patientGender = GENDER_LIST_ONBOARDING?.find(
        (item) => item.value === therapyReport?.patientGender,
      )?.label
      break

    case EVENT_TYPE_ASSESSMENT:
      patientName =
        assessmentReport?.patientName ??
        bookedEventFinalData?.patientName ??
        bookedEventFinalData?.patientPrefill?.name
      patientAge = getAgeFromDobWithTime(
        assessmentReport?.patientDateOfBirth ??
          bookedEventFinalData?.patientDateOfBirth ??
          bookedEventFinalData?.patientPrefill?.vital?.dateOfBirth,
      )
      patientGender = GENDER_LIST_ONBOARDING?.find(
        (item) =>
          item.value ===
          (assessmentReport?.patientGender ??
            bookedEventFinalData?.patientGender ??
            bookedEventFinalData?.patientPrefill?.vital?.gender),
      )?.label
      assessmentTitleForTop =
        assessmentReport?.assessmentRuns?.[0]?.clinicalAssessmentTitle ??
        assessmentReport?.assessmentRuns?.[0]?.psychAssessmentTitle ??
        assessmentReport?.assessmentRuns?.[0]?.assessmentTitle ??
        assessmentReport?.title ??
        'Assessment'
      break

    case EVENT_TYPE_SCREENING:
      finalObject.left = `Screening`
      finalObject.right = `${screeningReport.patientName} (${screeningReport?.patientAge},${
        GENDER_LIST_ONBOARDING?.find((item) => item.value === screeningReport.patientGender)?.label
      })`
      break

    default:
      break
  }
  if (eventType === EVENT_TYPE_SCREENING) {
    return finalObject
  }

  if (eventType === EVENT_TYPE_ASSESSMENT && assessmentTitleForTop) {
    let patientInfo = patientName || ''
    if (patientInfo && (patientAge || patientGender)) {
      const details = []
      if (patientAge) details.push(`${patientAge} Y`)
      if (patientGender) details.push(patientGender)
      patientInfo += ` (${details.join(', ')})`
    }
    finalObject.left = patientInfo
      ? `${assessmentTitleForTop} — ${patientInfo}`
      : assessmentTitleForTop
  } else if (patientName) {
    let patientInfo = patientName
    if (patientAge || patientGender) {
      const details = []
      if (patientAge) details.push(`${patientAge} Y`)
      if (patientGender) details.push(patientGender)
      patientInfo += ` (${details.join(', ')})`
    }
    finalObject.left = patientInfo
  } else {
    finalObject.left = ''
  }

  return finalObject
}

export function useBottomHeadingText({
  eventType,
  prescriptionData,
  therapyReport,
  screeningReport,
  assessmentReport,
  consultationDetails,
  startTime,
  endTime,
  isOffline,
}) {
  let finalObject = {
    left: '',
    center: '',
    right: '',
  }

  const timing = isOffline
    ? ''
    : ` | ${dayjs.utc(startTime).local().format('DD MMM YYYY')}, ${dayjs
        .utc(startTime)
        .local()
        .format('h:mma')} - ${dayjs.utc(endTime).local().format('h:mma')}`

  switch (eventType) {
    case EVENT_TYPE_CONSULTATION:
      finalObject.left = prescriptionData?.patientPrefill?.patientNumber || '--'
      finalObject.center = prescriptionData
        ? `${
            prescriptionData.patientPrefill?.mobile ||
            prescriptionData.patientPrefill?.email ||
            '--'
          }`
        : ''
      finalObject.right = timing
      break
    case EVENT_TYPE_THERAPY_SESSION:
      finalObject.left = therapyReport?.patientNumber || '--'
      finalObject.center = ''
      finalObject.right = timing
      break
    case EVENT_TYPE_ASSESSMENT:
      finalObject.left = assessmentReport?.patientNumber ?? '--'
      finalObject.center = ''
      finalObject.right = timing
      break
    case EVENT_TYPE_SCREENING:
      finalObject.left = `${screeningReport?.patientNumber}`
      finalObject.center = `Requested by: ${consultationDetails?.doctorName}`
      finalObject.right = timing
      break
    default:
      finalObject.left =
        prescriptionData?.patientPrefill?.patientNumber ||
        therapyReport?.patientNumber ||
        assessmentReport?.patientNumber ||
        '--'
      finalObject.center = prescriptionData
        ? ` | ${
            prescriptionData.patientPrefill?.mobile ||
            prescriptionData.patientPrefill?.email ||
            '--'
          }`
        : ''
      finalObject.right = timing
      break
  }

  return finalObject
}

export const LiveSessionHeaderTop = ({ topTextData, onDebugClick }) => {
  return (
    <LSVideoPartHeaderHeading onClick={onDebugClick}>
      {topTextData.left} | {topTextData.right}
    </LSVideoPartHeaderHeading>
  )
}

export const LiveSessionHeaderBottom = ({ bottomTextData }) => {
  const parts = [bottomTextData?.left, bottomTextData?.center, bottomTextData?.right].filter(
    Boolean,
  )

  return (
    <LSVideoPartHeaderHeading style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
      {parts.join(' | ')}
    </LSVideoPartHeaderHeading>
  )
}
